import { useCoverageBetaStore } from "@/store/useCoverageBetaStore";
import { focusQaItem } from "@/utils/coverage-beta-focus";
import { useEffect } from "react";

/**
 * Frames the query image on the Coverage Beta QA pager's current item
 * whenever it changes — whatever triggered the change: the panel's chevrons,
 * "Mark as Correct" auto-advancing, a sidebar row click, a canvas box click,
 * or the pager being seeded on initial hydration. Sidebar/box clicks already
 * call the focus helper directly for an instant response; this effect is
 * what covers every other trigger (a second, redundant call from those click
 * handlers is harmless — see coverage-beta-focus.ts).
 *
 * `isQueryImageReady` must reflect the query `<img>` having actually loaded,
 * not just `pinchRef` existing: the box-containing wrapper has zero size
 * until the image paints, so focusing any earlier would measure a zero-size
 * element. See QueryImageRelated, the only caller.
 */
export const useFocusActiveQaItem = (isQueryImageReady: boolean) => {
  const isQaMode = useCoverageBetaStore((s) => s.isQaMode);
  const activeQaItemKey = useCoverageBetaStore((s) => s.activeQaItemKey);
  const pinchRef = useCoverageBetaStore((s) => s.pinchRef);

  useEffect(() => {
    if (!isQaMode || !activeQaItemKey || !pinchRef) return;
    if (!isQueryImageReady) return;
    // The target box can mount in the same commit as the key change; measure
    // it a frame later, once it has actually laid out.
    const raf = requestAnimationFrame(() => {
      // `showBoxes` is read here rather than subscribed to on purpose. With the
      // boxes hidden there is no element to frame, so the focus is skipped —
      // but as a dependency it also re-ran this effect when the boxes came
      // back, snapping the view to the active item on nothing more than a
      // "hide boxes" / "show boxes" round trip. Only a pager change should
      // move the camera.
      if (!useCoverageBetaStore.getState().showBoxes) return;
      focusQaItem(activeQaItemKey);
    });
    return () => cancelAnimationFrame(raf);
  }, [isQaMode, activeQaItemKey, pinchRef, isQueryImageReady]);
};
